import { localeForLanguage, THOUSANDS_GROUPING } from './numberFormatting';
import { formatUnitPrice } from './money';

/**
 * Currency utility functions for formatting amounts with currency symbols
 */

/**
 * Get the currency symbol for a given currency code
 * @param currency - The currency code (USD, EUR, PLN, etc.)
 * @returns The currency symbol
 */
export const getCurrencySymbol = (currency: string): string => {
  const symbols: Record<string, string> = {
    USD: '$',
    EUR: '€',
    PLN: 'zł',
  };
  
  return symbols[currency] || currency;
};

/**
 * Format an amount with the appropriate currency symbol
 * @param amount - The numeric amount to format (can be string or null/undefined)
 * @param currency - The currency code (USD, EUR, PLN, etc.)
 * @returns The formatted currency string
 */
export const formatCurrency = (amount: number | string | null | undefined, currency: string = 'PLN'): string => {
  const symbol = getCurrencySymbol(currency);
  // Always grouped (owner/staging v3.13): „6 827,05 zł” next to „13 248,00 zł”, see THOUSANDS_GROUPING.
  const formatted = formatAmountGrouped(amount);

  if (currency === 'PLN') {
    return `${formatted} ${symbol}`;
  }
  return `${symbol}${formatted}`;
};

/**
 * Format a per-unit price with the appropriate currency symbol: 2 decimal
 * places after roundUnitPrice (owner 2026-10-07 — unit prices and amounts
 * are both 2dp; a stored legacy 4dp price shows as it would be saved). Symbol
 * placement mirrors formatCurrency so the two read consistently side by
 * side (a unit price column next to a total column).
 * @param amount - The numeric unit price (can be string or null/undefined)
 * @param currency - The currency code (USD, EUR, PLN, etc.)
 * @param language - The active i18n language (defaults to Polish)
 * @returns The formatted unit price string
 */
export const formatCurrencyUnitPrice = (
  amount: number | string | null | undefined,
  currency: string = 'PLN',
  language?: string,
): string => {
  const symbol = getCurrencySymbol(currency);
  const numericAmount = parseFloat(String(amount ?? 0)) || 0;
  const formatted = formatUnitPrice(numericAmount, language);

  if (currency === 'PLN') {
    return `${formatted} ${symbol}`;
  }
  return `${symbol}${formatted}`;
};

/** The number part of `formatCurrency` (pl-PL, 2dp, always grouped), without a unit. */
export function formatAmountGrouped(amount: number | string | null | undefined): string {
  const numericAmount = parseFloat(String(amount ?? 0)) || 0;
  return numericAmount.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    ...THOUSANDS_GROUPING,
  } as Intl.NumberFormatOptions);
}

/**
 * Unit after a money amount on the transport surfaces: „zł” for PLN, like the
 * rest of the app (formatCurrency), the ISO code for a foreign currency
 * („428,91 zł”, „120,00 EUR”). Owner/staging v3.14: the truck card mixed
 * „PLN” and „zł” for the same money.
 */
export const moneyUnit = (currencyCode: string): string => (
  !currencyCode || currencyCode === 'PLN' ? getCurrencySymbol('PLN') : currencyCode
);

/**
 * Format a numeric amount with pl-PL grouping (2dp, NBSP thousands
 * separator) and its unit from `moneyUnit` (e.g. "1 234,56 zł",
 * "120,00 EUR") — the single home for transport-surface money text: rate/total
 * inputs, the contract-sentence Alert, the truck card (TransportRouteDetail,
 * load table, trailer, freight key bar) and the TransportRoutes list.
 * @param value - The numeric amount to format
 * @param currencyCode - The ISO currency code (USD, EUR, PLN, etc.)
 * @returns The formatted "1 234,56 zł"-style string
 */
export const formatMoneyPl = (value: number, currencyCode: string): string =>
  // useGrouping 'always': Polish CLDR sets minimumGroupingDigits=2, so plain
  // pl-PL formatting leaves 4-digit amounts UNGROUPED ("4500,00") while antd
  // Statistic (explicit groupSeparator prop) always groups ("4 500,00") — the
  // two would visibly disagree on the same transport detail page for the
  // 1000–9999 range (orchestrator decision, S36 follow-up 2026-07-07).
  `${value.toLocaleString('pl-PL', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: 'always',
  } as Intl.NumberFormatOptions)} ${moneyUnit(currencyCode)}`;

/** Format a transport amount with localized separators and the `moneyUnit` suffix. */
export const formatMoneyLocalized = (
  value: number,
  currencyCode: string,
  language?: string,
): string => `${value.toLocaleString(localeForLanguage(language), {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
  useGrouping: 'always',
} as Intl.NumberFormatOptions)} ${moneyUnit(currencyCode)}`;

/** Format general financial UI in the active application language. */
export const formatCurrencyLocalized = (
  amount: number | string | null | undefined,
  currency = 'PLN',
  language?: string,
): string => formatCurrencyWithLocale(amount, currency, localeForLanguage(language));

/**
 * Format currency with proper locale formatting
 * @param amount - The numeric amount to format (can be string or null/undefined)
 * @param currency - The currency code
 * @param locale - The locale string (e.g., 'en-US', 'pl-PL')
 * @returns The formatted currency string
 */
export const formatCurrencyWithLocale = (
  amount: number | string | null | undefined,
  currency: string = 'PLN',
  locale?: string
): string => {
  // Convert to number and handle null/undefined/invalid values
  const numericAmount = parseFloat(String(amount ?? 0)) || 0;

  try {
    // Try to use the Intl.NumberFormat for better formatting
    const formatter = new Intl.NumberFormat(locale || 'en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
      ...THOUSANDS_GROUPING,
    } as Intl.NumberFormatOptions);
    return formatter.format(numericAmount);
  } catch (error) {
    // Fallback to simple formatting if Intl is not supported or currency is invalid
    return formatCurrency(numericAmount, currency);
  }
};

/**
 * Locale-aware compact currency (e.g. "1,2 mln zł" / "PLN 1.2M") — single
 * home for the CFO-dashboard-style KPI figures: Profitability.tsx's
 * "Bilans operacyjny" hero/satellites, FixedCosts.tsx's hero/satellites, and
 * the Profitability "Przegląd" overview hero/trend/breakdown (all previously
 * carried their own byte-identical copy of this helper). Detail tables keep
 * full precision via formatCurrency/formatCurrencyLocalized.
 * @param amount - The numeric amount to format
 * @param language - The active i18n language (defaults to Polish)
 * @returns The compact-notation currency string
 */
export const formatCompactPLN = (amount: number, language?: string): string => {
  const locale = localeForLanguage(language);
  try {
    return new Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: 1,
      style: 'currency',
      currency: 'PLN',
      currencyDisplay: locale === 'pl-PL' ? 'symbol' : 'code',
    }).format(amount);
  } catch {
    return formatCurrency(amount, 'PLN');
  }
};

/**
 * Parse a currency string to get the numeric value
 * @param currencyString - The formatted currency string
 * @returns The numeric value
 */
export const parseCurrency = (currencyString: string): number => {
  // Remove all non-numeric characters except decimal point and minus sign
  const cleanedString = currencyString.replace(/[^0-9.-]/g, '');
  return parseFloat(cleanedString) || 0;
};
